import type { Block, Source } from "../contract"
import type { TurnView } from "../store"
import { walkBlocks } from "./format"

/** One numbered entry of the "Authorities" list that ends a copied or printed answer. */
export interface Authority {
  n: number
  id: string
  title: string
  citation: string
  url: string
}

type AnswerParts = Pick<TurnView, "plan" | "sections" | "sources">

/** The source ids a block cites, in reading order. */
export function citedSourceIds(b: Block): string[] {
  if (b.type === "para") return (b.cites ?? []).map((c) => c.source_id)
  if (b.type === "quote") return [b.source_id]
  if (b.type === "tally") return (b.cases ?? []).map((c) => c.source_id)
  return []
}

/**
 * Every source the answer cites, numbered across the whole answer by first mention. Sections are read in
 * plan order, so the numbers follow the reading order a reader meets them in.
 */
export function authorities(view: AnswerParts): Authority[] {
  const seen = new Set<string>()
  const out: Authority[] = []
  const sectionIds = view.plan?.length
    ? view.plan.map((p) => p.id)
    : Object.keys(view.sections)
  for (const id of sectionIds) {
    const section = view.sections[id]
    if (!section) continue
    walkBlocks(section.blocks, (b) => {
      for (const sid of citedSourceIds(b)) {
        const src = view.sources[sid]
        if (!src || seen.has(sid)) continue
        seen.add(sid)
        out.push(toAuthority(sid, out.length + 1, src))
      }
    })
  }
  return out
}

function toAuthority(id: string, n: number, src: Source): Authority {
  return {
    n,
    id,
    title: (src.name || src.label || "").trim(),
    citation: (src.citation || "").trim(),
    url: (src.full_text_url || "").trim(),
  }
}

/** One list line: "1. <title>, <citation> <url>", with the citation and link left out when missing. */
export function authorityLine(a: Authority): string {
  const head = a.citation ? `${a.title}, ${a.citation}` : a.title
  return `${a.n}. ${[head, a.url].filter(Boolean).join(" ")}`
}

/** The "Authorities" block as plain text lines for the copied answer; empty when nothing is cited. */
export function authoritiesText(view: AnswerParts): string[] {
  const list = authorities(view)
  if (!list.length) return []
  return ["Authorities", ...list.map(authorityLine)]
}
